import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Animated contact scene: people reaching Akas by phone, email, visit, and
 * message — same visual language as the home hero activity.
 */

type Look = { shirt: string; pants?: string; skin?: string; hair?: string };

const SKIN = ['#f3c7a5', '#d9a47c', '#b97f57', '#eab78f'];
const HAIR = ['#243044', '#3b2a20', '#111827', '#5b3a24'];
const PRIMARY = '#008ecc';

const look = (shirt: string, i: number, pants = '#334155'): Look => ({
  shirt,
  pants,
  skin: SKIN[i % SKIN.length],
  hair: HAIR[i % HAIR.length],
});

const delay = (s: number): React.CSSProperties => ({ animationDelay: `${s}s` });
const origin = (x: number, y: number): React.CSSProperties => ({ transformOrigin: `${x}px ${y}px` });

const Head: React.FC<{ cx: number; cy: number; l: Look }> = ({ cx, cy, l }) => (
  <g>
    <circle cx={cx} cy={cy} r={7.5} fill={l.skin} />
    <path
      d={`M${cx - 7.6} ${cy} A7.6 7.6 0 0 1 ${cx + 7.6} ${cy} Q${cx} ${cy - 4} ${cx - 7.6} ${cy}Z`}
      fill={l.hair}
    />
  </g>
);

const StandingPerson: React.FC<{
  l: Look;
  walking?: boolean;
  arm?: 'phone' | 'wave' | 'laptop';
  d?: number;
}> = ({ l, walking, arm, d = 0 }) => (
  <g>
    <ellipse cx={0} cy={1} rx={10} ry={2.5} fill="rgba(20,40,60,0.12)" />
    <rect
      className={walking ? 'leg-a' : undefined}
      x={-6}
      y={-25}
      width={5}
      height={25}
      rx={2.5}
      fill={l.pants}
      style={{ ...origin(-3.5, -24), ...delay(d) }}
    />
    <rect
      className={walking ? 'leg-b' : undefined}
      x={1}
      y={-25}
      width={5}
      height={25}
      rx={2.5}
      fill={l.pants}
      style={{ ...origin(3.5, -24), ...delay(d) }}
    />
    <rect x={-9} y={-48} width={18} height={26} rx={7.5} fill={l.shirt} />
    <Head cx={0} cy={-56} l={l} />
    {arm === 'phone' && (
      <g className="talk" style={{ ...origin(-1, -46), ...delay(d) }}>
        <rect x={-3} y={-60} width={5} height={16} rx={2.5} fill={l.shirt} />
        <rect x={1} y={-66} width={4} height={10} rx={1.5} fill="#1f2937" />
      </g>
    )}
    {arm === 'wave' && (
      <g className="wave" style={{ ...origin(8, -43), ...delay(d) }}>
        <rect x={6} y={-48} width={18} height={5} rx={2.5} fill={l.shirt} />
        <circle cx={24} cy={-45.5} r={2.6} fill={l.skin} />
      </g>
    )}
    {arm === 'laptop' && (
      <g>
        <rect x={-18} y={-34} width={36} height={3} rx={1} fill="#94a3b8" />
        <rect x={-16} y={-52} width={32} height={20} rx={2} fill="#1e293b" />
        <rect className="screen" x={-13} y={-49} width={26} height={14} rx={1} fill="#e0f2fe" style={delay(d)} />
        <rect x={-4} y={-45} width={14} height={2} rx={1} fill="#38bdf8" />
        <rect x={-4} y={-40} width={10} height={2} rx={1} fill="#94a3b8" />
      </g>
    )}
  </g>
);

const SeatedWorker: React.FC<{
  x: number;
  y: number;
  l: Look;
  pose?: 'type' | 'phone' | 'listen';
  flip?: boolean;
  d?: number;
}> = ({ x, y, l, pose = 'type', flip, d = 0 }) => (
  <g transform={`translate(${x} ${y})${flip ? ' scale(-1 1)' : ''}`}>
    <rect x={-16} y={-44} width={4} height={26} rx={2} fill="#64748b" />
    <rect x={-15} y={-22} width={21} height={4} rx={2} fill="#64748b" />
    <rect x={-6} y={-18} width={3} height={16} fill="#94a3b8" />
    <rect x={-14} y={-3} width={19} height={3} rx={1.5} fill="#94a3b8" />
    <rect x={10} y={-30} width={64} height={4} rx={1} fill="#cbd5e1" />
    <rect x={66} y={-26} width={4} height={26} fill="#cbd5e1" />
    <rect x={46} y={-36} width={3} height={6} fill="#475569" />
    <rect x={32} y={-57} width={32} height={22} rx={2.5} fill="#1e293b" />
    <g className="screen" style={delay(d)}>
      <rect x={34} y={-56} width={28} height={20} rx={2} fill="#f8fafc" />
      <rect className="line" x={37} y={-52} width={18} height={2} rx={1} fill="#22c55e" style={{ ...origin(37, -51), ...delay(d) }} />
      <rect className="line" x={37} y={-47} width={22} height={2} rx={1} fill="#cbd5e1" style={{ ...origin(37, -46), ...delay(d + 0.3) }} />
      <rect className="line" x={37} y={-42} width={14} height={2} rx={1} fill="#cbd5e1" style={{ ...origin(37, -41), ...delay(d + 0.6) }} />
    </g>
    <rect x={15} y={-32} width={15} height={2} rx={1} fill="#64748b" />
    <rect x={-8} y={-27} width={21} height={7} rx={3.5} fill={l.pants} />
    <rect x={7} y={-27} width={6} height={27} rx={3} fill={l.pants} />
    <g className="breathe" style={{ ...origin(0, -22), ...delay(d) }}>
      <rect x={-10} y={-52} width={17} height={28} rx={7.5} fill={l.shirt} />
      <g className={pose === 'listen' ? 'nod' : 'head'} style={{ ...origin(-2, -54), ...delay(d) }}>
        <Head cx={-2} cy={-61} l={l} />
      </g>
      {pose === 'phone' ? (
        <g className="talk" style={{ ...origin(-1, -46), ...delay(d) }}>
          <rect x={-3} y={-60} width={5} height={16} rx={2.5} fill={l.shirt} />
          <rect x={1} y={-66} width={4} height={10} rx={1.5} fill="#1f2937" />
        </g>
      ) : (
        <g className={pose === 'type' ? 'type' : 'rest'} style={{ ...origin(-2, -44), ...delay(d * 0.7) }}>
          <rect x={-4} y={-46} width={22} height={5} rx={2.5} fill={l.shirt} />
          <circle cx={18.5} cy={-43.5} r={2.6} fill={l.skin} />
        </g>
      )}
    </g>
  </g>
);

const Walker: React.FC<{
  x: number;
  y: number;
  dist: number;
  dur: number;
  l: Look;
  arm?: 'wave' | 'phone';
  d?: number;
}> = ({ x, y, dist, dur, l, arm, d = 0 }) => (
  <g transform={`translate(${x} ${y})`}>
    <g
      className="pace"
      style={{ ['--d' as string]: `${dist}px`, animationDuration: `${dur}s`, ...delay(d) }}
    >
      <StandingPerson l={l} walking arm={arm} d={d} />
    </g>
  </g>
);

const Plant: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <rect x={-7} y={-14} width={14} height={14} rx={2} fill="#94a3b8" />
    <circle cx={-5} cy={-22} r={7} fill="#4ade80" />
    <circle cx={5} cy={-24} r={7} fill="#22c55e" />
    <circle cx={0} cy={-30} r={7} fill="#16a34a" />
  </g>
);

const SignPlate: React.FC<{ x: number; y: number; w: number; label: string }> = ({ x, y, w, label }) => (
  <g>
    <rect x={x - w / 2} y={y} width={w} height={22} rx={4} fill={PRIMARY} />
    <text x={x} y={y + 15.5} textAnchor="middle" fontSize={12.5} fontWeight={700} fill="#fff" letterSpacing="0.06em">
      {label}
    </text>
  </g>
);

const ChannelBadge: React.FC<{
  x: number;
  y: number;
  label: string;
  color: string;
  icon: 'phone' | 'mail' | 'pin' | 'chat';
  d: number;
}> = ({ x, y, label, color, icon, d }) => {
  const w = Math.max(110, label.length * 7.2 + 48);
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className="chip" style={delay(d)}>
        <rect x={-w / 2} y={-18} width={w} height={36} rx={18} fill="#fff" filter="url(#contactChipShadow)" />
        <circle cx={-w / 2 + 18} cy={0} r={11} fill={color} />
        {icon === 'phone' && (
          <rect x={-w / 2 + 14.5} y={-6} width={7} height={12} rx={1.8} fill="#fff" />
        )}
        {icon === 'mail' && (
          <g fill="none" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <rect x={-w / 2 + 12} y={-5} width={12} height={9} rx={1.2} />
            <path d={`M${-w / 2 + 12} -4.2 L${-w / 2 + 18} 0.5 L${-w / 2 + 24} -4.2`} />
          </g>
        )}
        {icon === 'pin' && (
          <g fill="#fff">
            <circle cx={-w / 2 + 18} cy={-2} r={3.2} />
            <path d={`M${-w / 2 + 18} 2 L${-w / 2 + 15.5} 8 H${-w / 2 + 20.5} Z`} />
          </g>
        )}
        {icon === 'chat' && (
          <g fill="#fff">
            <rect x={-w / 2 + 12.5} y={-5.5} width={11} height={8} rx={2} />
            <path d={`M${-w / 2 + 15} 2.5 L${-w / 2 + 17.5} 6 L${-w / 2 + 20} 2.5 Z`} />
          </g>
        )}
        <text x={-w / 2 + 36} y={5} fontSize={13} fontWeight={600} fill="#1a2b3c">
          {label}
        </text>
      </g>
    </g>
  );
};

const LINKS = [
  { id: 'contact-phone', d: 'M170 420 Q280 300 480 340' },
  { id: 'contact-mail', d: 'M170 560 Q300 480 480 400' },
  { id: 'contact-visit', d: 'M920 700 Q780 620 620 520' },
  { id: 'contact-chat', d: 'M980 280 Q820 300 620 360' },
];

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
};

const ContactAnimation: React.FC = () => {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <Wrap aria-hidden="true">
      <Scene viewBox="0 0 1100 720" preserveAspectRatio="xMidYMid meet" focusable="false">
        <defs>
          <linearGradient id="contactSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e4f0f8" />
            <stop offset="1" stopColor="#f5f9fc" />
          </linearGradient>
          <linearGradient id="contactGlass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.35)" />
            <stop offset="0.45" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <filter id="contactChipShadow" x="-20%" y="-50%" width="140%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#14283c" floodOpacity="0.14" />
          </filter>
        </defs>

        <rect width="1100" height="720" rx="12" fill="url(#contactSky)" />

        <g className="cloud" style={{ animationDuration: '55s' }}>
          <ellipse cx="180" cy="90" rx="55" ry="16" fill="#fff" opacity="0.8" />
          <ellipse cx="215" cy="78" rx="35" ry="18" fill="#fff" opacity="0.8" />
        </g>
        <g className="cloud" style={{ animationDuration: '70s', ...delay(-20) }}>
          <ellipse cx="820" cy="70" rx="70" ry="18" fill="#fff" opacity="0.75" />
          <ellipse cx="860" cy="56" rx="42" ry="20" fill="#fff" opacity="0.75" />
        </g>

        <g fill="#dde9f2">
          <rect x="40" y="380" width="48" height="180" />
          <rect x="100" y="340" width="36" height="220" />
          <rect x="980" y="360" width="40" height="200" />
          <rect x="1030" y="400" width="50" height="160" />
        </g>
        <path d="M0 540 Q280 500 550 530 T1100 510 V560 H0Z" fill="#d6e8d9" />

        <rect x="0" y="560" width="1100" height="10" fill="#e2e8f0" />
        <rect x="0" y="570" width="1100" height="48" fill="#94a3b8" />
        <line x1="0" y1="594" x2="1100" y2="594" stroke="#f8fafc" strokeWidth="2.5" strokeDasharray="22 18" className="lane" />
        <rect x="0" y="618" width="1100" height="102" fill="#cfe5d3" />

        {/* Phone booth / caller */}
        <g>
          <rect x="70" y="400" width="100" height="160" rx="6" fill="#ffffff" stroke="#c9d8e4" />
          <rect x="82" y="412" width="76" height="110" rx="3" fill="#eef6fb" />
          <SignPlate x={120} y={372} w={78} label="PHONE" />
          <g transform="translate(120 560)">
            <StandingPerson l={look('#0ea5e9', 1)} arm="phone" d={0.2} />
          </g>
          {/* Ring ripples */}
          <circle className="ring" cx="120" cy="470" r="18" fill="none" stroke={PRIMARY} strokeWidth="2" style={delay(0)} />
          <circle className="ring" cx="120" cy="470" r="18" fill="none" stroke={PRIMARY} strokeWidth="2" style={delay(0.8)} />
          <rect x="82" y="412" width="76" height="110" fill="url(#contactGlass)" />
        </g>

        {/* Email / laptop person */}
        <g>
          <rect x="70" y="200" width="120" height="130" rx="6" fill="#ffffff" stroke="#c9d8e4" />
          <rect x="82" y="212" width="96" height="100" rx="3" fill="#f8fafc" />
          <SignPlate x={130} y={172} w={78} label="EMAIL" />
          <g transform="translate(130 312)">
            <StandingPerson l={look('#7c3aed', 2)} arm="laptop" d={0.5} />
          </g>
          {/* Flying envelope */}
          <g className="envelope" style={delay(0)}>
            <rect x="210" y="240" width="28" height="18" rx="2" fill="#fff" stroke={PRIMARY} strokeWidth="1.5" />
            <path d="M210 240 L224 250 L238 240" fill="none" stroke={PRIMARY} strokeWidth="1.5" />
          </g>
          <rect x="82" y="212" width="96" height="100" fill="url(#contactGlass)" />
        </g>

        {/* Head office / reception */}
        <g>
          <rect x="420" y="200" width="16" height="22" rx="3" fill="#dbe5ee" />
          <rect x="620" y="210" width="14" height="16" rx="3" fill="#dbe5ee" />
          <rect x="390" y="220" width="320" height="12" rx="2" fill="#cbd9e5" />
          <rect x="400" y="232" width="300" height="328" fill="#ffffff" stroke="#c9d8e4" />
          <rect x="412" y="244" width="276" height="120" fill="#eef6fb" />
          <rect x="412" y="372" width="276" height="8" fill="#d5e2ec" />
          <rect x="412" y="380" width="276" height="168" fill="#f1f7fb" />
          <SignPlate x={550} y={178} w={120} label="AKAS OFFICE" />

          {/* Upper: support agents */}
          <SeatedWorker x={430} y={372} l={look('#1d4ed8', 0)} pose="phone" d={0.1} />
          <SeatedWorker x={540} y={372} l={look('#0f766e', 3)} pose="type" d={0.7} />
          <SeatedWorker x={640} y={372} l={look('#db2777', 1)} pose="listen" flip d={1.2} />

          {/* Ground: reception desk */}
          <rect x="500" y="480" width="120" height="10" rx={2} fill="#cbd5e1" />
          <rect x="510" y="490" width={100} height={58} rx={3} fill="#e2e8f0" />
          <rect x="540" y="500" width={40} height={24} rx={2} fill="#1e293b" />
          <rect className="blink" x="546" y="506" width={28} height={12} rx={1} fill="#22c55e" />
          <SeatedWorker x={430} y={560} l={look('#be123c', 2)} pose="type" d={0.4} />
          <Plant x={680} y={560} s={0.85} />
          <g transform="translate(600 560)">
            <StandingPerson l={look('#0891b2', 0)} arm="wave" d={0.9} />
          </g>

          <rect x="412" y="244" width="276" height="304" fill="url(#contactGlass)" />
        </g>

        {/* Visit / walk-in */}
        <g>
          <SignPlate x={920} y={470} w={72} label="VISIT" />
          <Walker x={860} y={560} dist={90} dur={8} l={look('#f97316', 1)} arm="wave" d={-1} />
          {/* Map pin */}
          <g className="pin" style={delay(0.3)}>
            <path d="M980 500 C980 480 1000 470 1000 490 C1000 505 980 525 980 525 C980 525 960 505 960 490 C960 470 980 480 980 500Z" fill="#ef4444" />
            <circle cx="980" cy="492" r="6" fill="#fff" />
          </g>
        </g>

        {/* Chat / message node */}
        <g>
          <rect x="880" y="160" width="140" height="100" rx="10" fill="#ffffff" stroke="#c9d8e4" />
          <SignPlate x={950} y={132} w={78} label="CHAT" />
          <rect x="900" y="182" width={70} height={14} rx={7} fill="#e0f2fe" />
          <rect className="bubble" x="920" y="204" width={56} height={14} rx={7} fill="#dcfce7" style={delay(0.4)} />
          <rect className="bubble" x="900" y="226" width={80} height={14} rx={7} fill="#e0f2fe" style={delay(0.9)} />
          <g transform="translate(1020 250) scale(0.85)">
            <StandingPerson l={look('#16a34a', 3)} arm="phone" d={1} />
          </g>
        </g>

        {/* Contact channel lines */}
        {LINKS.map((link, i) => (
          <path
            key={link.id}
            id={link.id}
            d={link.d}
            fill="none"
            stroke="rgba(0,142,204,0.4)"
            strokeWidth={2.5}
            strokeDasharray="6 10"
            strokeLinecap="round"
            className="flow"
            style={delay(i * -0.5)}
          />
        ))}
        {!reducedMotion &&
          LINKS.flatMap((link, i) =>
            [0, 1].map((k) => (
              <g key={`${link.id}-${k}`}>
                <circle r={8} fill={PRIMARY} opacity={0.18}>
                  <animateMotion
                    dur={`${3 + i * 0.4}s`}
                    begin={`${k * 1.5 + i * 0.35}s`}
                    repeatCount="indefinite"
                    keyPoints={k ? '1;0' : '0;1'}
                    keyTimes="0;1"
                    calcMode="linear"
                  >
                    <mpath href={`#${link.id}`} />
                  </animateMotion>
                </circle>
                <circle r={3.5} fill={PRIMARY}>
                  <animateMotion
                    dur={`${3 + i * 0.4}s`}
                    begin={`${k * 1.5 + i * 0.35}s`}
                    repeatCount="indefinite"
                    keyPoints={k ? '1;0' : '0;1'}
                    keyTimes="0;1"
                    calcMode="linear"
                  >
                    <mpath href={`#${link.id}`} />
                  </animateMotion>
                </circle>
              </g>
            )),
          )}

        {/* Channel notification chips */}
        <ChannelBadge x={120} y={350} label="Call us" color="#0ea5e9" icon="phone" d={0} />
        <ChannelBadge x={130} y={150} label="Email us" color="#7c3aed" icon="mail" d={2} />
        <ChannelBadge x={550} y={150} label="We reply fast" color="#008ecc" icon="chat" d={4} />
        <ChannelBadge x={920} y={440} label="Visit office" color="#ef4444" icon="pin" d={6} />
      </Scene>
    </Wrap>
  );
};

const type = keyframes`
  0%, 100% { transform: rotate(-5deg); }
  50% { transform: rotate(5deg); }
`;

const breathe = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-1px); }
`;

const headTurn = keyframes`
  0%, 70%, 100% { transform: rotate(0deg); }
  78%, 90% { transform: rotate(-8deg); }
`;

const nod = keyframes`
  0%, 40%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(9deg); }
  60% { transform: rotate(0deg); }
  70% { transform: rotate(7deg); }
`;

const talk = keyframes`
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-6deg); }
`;

const wave = keyframes`
  0%, 100% { transform: rotate(0deg); }
  30% { transform: rotate(-28deg); }
  60% { transform: rotate(12deg); }
`;

const lineGrow = keyframes`
  0%, 100% { transform: scaleX(0.45); }
  50% { transform: scaleX(1); }
`;

const screenGlow = keyframes`
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
`;

const pace = keyframes`
  0% { transform: translateX(0) scaleX(1); }
  45% { transform: translateX(var(--d)) scaleX(1); }
  50% { transform: translateX(var(--d)) scaleX(-1); }
  95% { transform: translateX(0) scaleX(-1); }
  100% { transform: translateX(0) scaleX(1); }
`;

const legA = keyframes`
  0%, 100% { transform: rotate(18deg); }
  50% { transform: rotate(-18deg); }
`;

const legB = keyframes`
  0%, 100% { transform: rotate(-18deg); }
  50% { transform: rotate(18deg); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const flow = keyframes`
  to { stroke-dashoffset: -64; }
`;

const lane = keyframes`
  to { stroke-dashoffset: -100; }
`;

const drift = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(80px); }
`;

const chip = keyframes`
  0%, 4% { opacity: 0; transform: translateY(10px) scale(0.92); }
  8%, 20% { opacity: 1; transform: translateY(0) scale(1); }
  26%, 100% { opacity: 0; transform: translateY(-14px) scale(1); }
`;

const ring = keyframes`
  0% { opacity: 0.55; transform: scale(0.55); }
  100% { opacity: 0; transform: scale(1.8); }
`;

const envelope = keyframes`
  0% { opacity: 0; transform: translate(0, 0); }
  15% { opacity: 1; }
  85% { opacity: 1; }
  100% { opacity: 0; transform: translate(220px, 40px); }
`;

const bubble = keyframes`
  0%, 100% { opacity: 0.45; transform: scaleX(0.85); }
  50% { opacity: 1; transform: scaleX(1); }
`;

const pin = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const Wrap = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(180deg, #eef6fb 0%, #f6fafc 100%);
`;

const Scene = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  min-height: 360px;
  font-family: inherit;

  .type { animation: ${type} 0.45s ease-in-out infinite; }
  .rest { transform: rotate(10deg); }
  .breathe { animation: ${breathe} 3.2s ease-in-out infinite; }
  .head { animation: ${headTurn} 6s ease-in-out infinite; }
  .nod { animation: ${nod} 4s ease-in-out infinite; }
  .talk { animation: ${talk} 2.4s ease-in-out infinite; }
  .wave { animation: ${wave} 2.2s ease-in-out infinite; }
  .screen { animation: ${screenGlow} 2.8s ease-in-out infinite; }
  .line { animation: ${lineGrow} 2.4s ease-in-out infinite; }
  .pace { animation: ${pace} 8s linear infinite; transform-origin: 0 0; }
  .leg-a { animation: ${legA} 0.6s ease-in-out infinite; }
  .leg-b { animation: ${legB} 0.6s ease-in-out infinite; }
  .blink { animation: ${blink} 1.4s ease-in-out infinite; }
  .flow { animation: ${flow} 1.6s linear infinite; }
  .lane { animation: ${lane} 3s linear infinite; }
  .cloud { animation: ${drift} 55s ease-in-out infinite; }
  .ring {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ring} 2.4s ease-out infinite;
  }
  .envelope {
    animation: ${envelope} 5s ease-in-out infinite;
  }
  .bubble {
    transform-box: fill-box;
    transform-origin: left center;
    animation: ${bubble} 2.8s ease-in-out infinite;
  }
  .pin {
    animation: ${pin} 2.4s ease-in-out infinite;
  }
  .chip {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${chip} 8s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; }
    .chip { opacity: 1; }
  }
`;

export default ContactAnimation;
